import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, AlertCircle, Loader2 } from 'lucide-react';

export default function PublishResults() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Retrieve the admin token from localStorage
      const token = localStorage.getItem("admin_token") || "";
      
      const response = await fetch('/api/api/admin/results/publish/status', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}` // Send token to backend
        }
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error("Unauthorized (401): Your session may have expired. Please log out and log in again.");
        }
        throw new Error(`Server Error (${response.status}): ${response.statusText}`);
      }
      
      const json = await response.json();
      setData(Array.isArray(json.statuses) ? json.statuses : []);
    } catch (err) {
      console.error("Fetch error:", err);
      // Display the actual error message rather than a generic one
      setError(err.message || "Unable to connect to server. Please ensure the backend is running.");
    } finally {
      setLoading(false);
    }
  }, []);

  const togglePublish = async (program, batch, semester, currentStatus) => {
    try {
      const token = localStorage.getItem("admin_token") || "";
      
      const response = await fetch('/api/api/admin/results/publish/toggle', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` // Send token for POST requests too
        },
        body: JSON.stringify({
          program,
          batch,
          semester,
          is_published: !currentStatus
        })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to update status (${response.status})`);
      }
      
      // Refresh list after successful toggle
      await fetchStatus();
    } catch (err) {
      console.error("Toggle error:", err);
      setError(`Toggle failed: ${err.message}. Please try again.`);
    }
  };

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Result Publication Manager</h1>
        <button 
          onClick={fetchStatus}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg transition disabled:opacity-50"
        >
          <RefreshCw size={16} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>
      
      {error && (
        <div className="flex items-center gap-3 p-4 mb-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}
      
      {loading && data.length === 0 ? (
        <div className="flex items-center justify-center p-10 text-gray-500">
          <Loader2 className="animate-spin mr-2" /> Loading data...
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden border border-gray-200">
          <table className="w-full text-left">
            <thead className="bg-gray-100 border-b">
              <tr>
                <th className="p-4 font-semibold text-gray-700">Batch</th>
                <th className="p-4 font-semibold text-gray-700">Program</th>
                <th className="p-4 font-semibold text-gray-700">Semester</th>
                <th className="p-4 font-semibold text-gray-700">Record Count</th>
                <th className="p-4 font-semibold text-gray-700">Status</th>
                <th className="p-4 font-semibold text-gray-700">Action</th>
              </tr>
            </thead>
            <tbody>
              {data.map((item, idx) => (
                <tr key={`${item.batch}-${item.program}-${item.semester}`} className="border-b hover:bg-gray-50 transition">
                  <td className="p-4">{item.batch}</td>
                  <td className="p-4">{item.program}</td>
                  <td className="p-4">{item.semester}</td>
                  <td className="p-4">{item.count}</td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded text-xs font-bold ${item.is_published ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                      {item.is_published ? 'PUBLISHED' : 'HIDDEN'}
                    </span>
                  </td>
                  <td className="p-4">
                    <button 
                      onClick={() => togglePublish(item.program, item.batch, item.semester, item.is_published)}
                      className={`px-4 py-2 rounded text-sm font-medium transition ${item.is_published ? 'bg-red-50 text-red-700 hover:bg-red-100' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                    >
                      {item.is_published ? 'Hide Results' : 'Publish Results'}
                    </button>
                  </td>
                </tr>
              ))}
              {data.length === 0 && !loading && !error && (
                <tr><td colSpan="6" className="p-8 text-center text-gray-500">No results have been uploaded yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
